import {reactive, computed, watch} from 'vue';

// Mirrors utils/LoyaltyProgramConstants.java — DEFAULT_SIGNUP_BONUS_POINTS and MAX_SIGNUP_BONUS_POINTS
// are the source of truth, and the server rejects anything above the ceiling regardless of what this
// file says. Defined once here (rather than repeated in the validator, the input's max attribute and
// the error copy) so raising the server-side ceiling is a one-line change on this side.
export const DEFAULT_SIGNUP_BONUS_POINTS = 250;
export const MAX_SIGNUP_BONUS_POINTS = 1000;

// Mirrors utils/DriverIdleConstants.java and the DB defaults on service_jurisdiction (#191). The
// server is the source of truth; these only pre-fill a brand-new jurisdiction so the form does not
// open on blanks that would then fail validation.
export const DEFAULT_IDLE_WARNING_MINUTES = 30;
export const DEFAULT_IDLE_AUTO_OFFLINE_MINUTES = 45;

// Mirrors DriverIdleConstants.MAX_THRESHOLD_MINUTES and MIN_RESPONSE_WINDOW_SECONDS. The gap rule is
// not cosmetic: DriverIdleService refuses to send a prompt with less than MIN_RESPONSE_WINDOW_SECONDS
// left on the clock, so a 44/45 window passes "warning < autoOffline" and then never prompts anybody
// — every driver in the jurisdiction is set offline with no warning. Server-side
// ServiceJurisdictionValidator.validateIdleWindow enforces the same thing and is the authority; this
// exists so the admin sees why before submitting.
export const MAX_IDLE_MINUTES = 720;
export const MIN_IDLE_GAP_SECONDS = 60;

// Mirrors utils/DispatchRadiusConstants.java and the DB default on
// service_jurisdiction.max_pickup_distance_meters (#197). The server is the source of truth and
// rejects anything outside the bounds regardless of what this file says. WARN_ABOVE is advisory only
// — it must never mark the field invalid, because EntityForm blocks save on an invalid field and
// widening the radius in a thin market is a legitimate thing for an admin to do.
export const DEFAULT_MAX_PICKUP_DISTANCE_METERS = 5000;
export const MIN_MAX_PICKUP_DISTANCE_METERS = 500;
export const MAX_MAX_PICKUP_DISTANCE_METERS = 15000;
export const WARN_ABOVE_PICKUP_DISTANCE_METERS = 8000;
// Mirrors utils/ReferralCampaignConstants.java and the DB defaults on service_jurisdiction (#196).
// The server is the authority; ServiceJurisdictionValidator rejects anything outside these bounds
// regardless of what this file says. The discount floor is 1 rather than 0 on purpose: a zero would
// validate, be snapshotted onto the reward ledger, and then never redeem — leaving the driver holding
// a reward they can never use. Switching driver referral rewards off is a redemption cap of 0.
export const DEFAULT_REFERRAL_DRIVER_DISCOUNT_PERCENT = 50;
export const DEFAULT_REFERRAL_DRIVER_MAX_REDEMPTIONS = 4;
export const DEFAULT_REFERRAL_RIDER_FIRST_RIDE_REFERRER_POINTS = 100;
export const DEFAULT_REFERRAL_RIDER_FIRST_RIDE_REFERRED_POINTS = 300;
export const DEFAULT_REFERRAL_RIDER_FIVE_RIDES_REFERRER_POINTS = 400;
export const DEFAULT_REFERRAL_RIDER_MAX_REWARDED_REFERRALS = 10;
export const MIN_REFERRAL_DISCOUNT_PERCENT = 1;
export const MAX_REFERRAL_DISCOUNT_PERCENT = 100;
export const MAX_REFERRAL_REWARD_CAP = 100;
export const MAX_REFERRAL_RIDER_POINTS = 2000;

// Same emptiness-before-comparison rule as isWholeMinute above: '' >= 0 is true in JS.
const isWholeInRange = (value, min, max) => value !== '' && value !== null && value !== undefined
    && Number.isInteger(Number(value)) && Number(value) >= min && Number(value) <= max;

// A blank number input yields '' rather than a number, '' >= 1 is true in JS, and v-model.number
// passes '' straight through, so the emptiness check has to come before any comparison. isInteger
// rather than isFinite because Jackson's ACCEPT_FLOAT_AS_INT truncates 30.7 to 30 server-side, which
// would report success for a value the admin never typed.
const isWholeMinute = (value) => value !== '' && value !== null && value !== undefined
    && Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= MAX_IDLE_MINUTES;

export default function ServiceJurisdiction(data = {}) {
    const state = reactive({
        id: data.id || '',
        name: data.name || '',
        description: data.description || '',
        active: data.active !== undefined ? data.active : true,
        serviceArea: data.serviceArea || null,
        geoZoneId: data.serviceArea?.id || data.geoZoneId || null,
        serviceAreaAllowance: data.serviceAreaAllowance || 10.0,
        commissionPercentage: data.commissionPercentage !== undefined ? data.commissionPercentage : 0.0,
        maxDrivers: data.maxDrivers !== undefined ? data.maxDrivers : null,
        pointsPerDollar: data.pointsPerDollar !== undefined ? data.pointsPerDollar : 1.00,
        signupBonusPoints: data.signupBonusPoints !== undefined ? data.signupBonusPoints : DEFAULT_SIGNUP_BONUS_POINTS,
        // Held as the API's own ISO-8601 (or null), because EntityForm.fetchEntity writes fetched
        // values straight into this state and PUTs it back verbatim. The datetime-local input format
        // is a display concern, converted in ServiceJurisdictionDetail.vue.
        signupBonusStartsOn: data.signupBonusStartsOn || '',
        signupBonusEndsOn: data.signupBonusEndsOn || '',
        idleWarningMinutes: data.idleWarningMinutes !== undefined
            ? data.idleWarningMinutes : DEFAULT_IDLE_WARNING_MINUTES,
        idleAutoOfflineMinutes: data.idleAutoOfflineMinutes !== undefined
            ? data.idleAutoOfflineMinutes : DEFAULT_IDLE_AUTO_OFFLINE_MINUTES,
        maxPickupDistanceMeters: data.maxPickupDistanceMeters !== undefined
            ? data.maxPickupDistanceMeters : DEFAULT_MAX_PICKUP_DISTANCE_METERS,
        referralDriverDiscountPercent: data.referralDriverDiscountPercent !== undefined
            ? data.referralDriverDiscountPercent : DEFAULT_REFERRAL_DRIVER_DISCOUNT_PERCENT,
        referralDriverMaxRedemptions: data.referralDriverMaxRedemptions !== undefined
            ? data.referralDriverMaxRedemptions : DEFAULT_REFERRAL_DRIVER_MAX_REDEMPTIONS,
        referralRiderFirstRideReferrerPoints: data.referralRiderFirstRideReferrerPoints !== undefined
            ? data.referralRiderFirstRideReferrerPoints : DEFAULT_REFERRAL_RIDER_FIRST_RIDE_REFERRER_POINTS,
        referralRiderFirstRideReferredPoints: data.referralRiderFirstRideReferredPoints !== undefined
            ? data.referralRiderFirstRideReferredPoints : DEFAULT_REFERRAL_RIDER_FIRST_RIDE_REFERRED_POINTS,
        referralRiderFiveRidesReferrerPoints: data.referralRiderFiveRidesReferrerPoints !== undefined
            ? data.referralRiderFiveRidesReferrerPoints : DEFAULT_REFERRAL_RIDER_FIVE_RIDES_REFERRER_POINTS,
        referralRiderMaxRewardedReferrals: data.referralRiderMaxRewardedReferrals !== undefined
            ? data.referralRiderMaxRewardedReferrals : DEFAULT_REFERRAL_RIDER_MAX_REWARDED_REFERRALS,
        createdAt: data.createdAt || null,
        updatedAt: data.updatedAt || null
    });

    const validations = {
        isNameValid: computed(() => !!state.name && state.name.trim().length > 0),
        isCommissionPercentageValid: computed(() => state.commissionPercentage >= 0 && state.commissionPercentage <= 100),
        // Mirrors the server-side margin assertion: rate x the 2x promo ceiling x $0.01 per point must
        // stay within $0.02 of reward cost per eligible dollar, so the rate itself caps at 1.00.
        // The emptiness check is load-bearing for the same reason as isSignupBonusPointsValid below:
        // clearing the input yields '', and '' >= 0 && '' <= 1.00 are both true in JS, so without it a
        // blank rate validates, Jackson coerces '' to null, and the service's "null means leave
        // unchanged" keeps the old rate while the admin sees a success toast.
        isPointsPerDollarValid: computed(() => {
            const rate = Number(state.pointsPerDollar);
            return state.pointsPerDollar !== '' && state.pointsPerDollar !== null
                && Number.isFinite(rate) && rate >= 0 && rate <= 1.00;
        }),
        // Mirrors LoyaltyValidator.validateSignupBonus and the promo-window ordering rule.
        // The emptiness check is load-bearing, not defensive: clearing a number input yields '',
        // v-model.number passes '' straight through (parseFloat('') is NaN, so Vue keeps the string),
        // and '' >= 0 is true in JS. Without it the form would accept a blank bonus, Jackson would
        // coerce '' to null, and the service's "null means leave unchanged" would silently keep the
        // old value while the save reported success.
        isSignupBonusPointsValid: computed(() => {
            const points = Number(state.signupBonusPoints);
            // isInteger, not isFinite: 250.7 passes every other check here, and Jackson's
            // ACCEPT_FLOAT_AS_INT (on by default) then truncates it to 250 server-side, so the admin
            // gets a success toast for an amount they did not type. The server cannot catch this --
            // by the time the DTO holds an Integer the fraction is already gone.
            return state.signupBonusPoints !== '' && state.signupBonusPoints !== null
                && state.signupBonusPoints !== undefined
                && Number.isInteger(points) && points >= 0 && points <= MAX_SIGNUP_BONUS_POINTS;
        }),
        isSignupBonusWindowValid: computed(() => !state.signupBonusStartsOn || !state.signupBonusEndsOn
            || new Date(state.signupBonusEndsOn) > new Date(state.signupBonusStartsOn)),
        // Same emptiness and isInteger guards as the signup bonus above, for the same two reasons:
        // '' >= 1 is true in JS, and Jackson's ACCEPT_FLOAT_AS_INT would silently truncate 30.7 to 30.
        isMaxPickupDistanceValid: computed(() => {
            const value = state.maxPickupDistanceMeters;
            return value !== '' && value !== null && value !== undefined
                && Number.isInteger(Number(value))
                && Number(value) >= MIN_MAX_PICKUP_DISTANCE_METERS
                && Number(value) <= MAX_MAX_PICKUP_DISTANCE_METERS;
        }),
        // Advisory, never a validity check: this must not feed isValid().
        isMaxPickupDistanceAboveAdvisory: computed(() =>
            Number(state.maxPickupDistanceMeters) > WARN_ABOVE_PICKUP_DISTANCE_METERS),
        isReferralDriverDiscountPercentValid: computed(() => isWholeInRange(
            state.referralDriverDiscountPercent, MIN_REFERRAL_DISCOUNT_PERCENT, MAX_REFERRAL_DISCOUNT_PERCENT)),
        isReferralDriverMaxRedemptionsValid: computed(() => isWholeInRange(
            state.referralDriverMaxRedemptions, 0, MAX_REFERRAL_REWARD_CAP)),
        isReferralRiderMaxRewardedReferralsValid: computed(() => isWholeInRange(
            state.referralRiderMaxRewardedReferrals, 0, MAX_REFERRAL_REWARD_CAP)),
        isReferralRiderFirstRideReferrerPointsValid: computed(() => isWholeInRange(
            state.referralRiderFirstRideReferrerPoints, 0, MAX_REFERRAL_RIDER_POINTS)),
        isReferralRiderFirstRideReferredPointsValid: computed(() => isWholeInRange(
            state.referralRiderFirstRideReferredPoints, 0, MAX_REFERRAL_RIDER_POINTS)),
        isReferralRiderFiveRidesReferrerPointsValid: computed(() => isWholeInRange(
            state.referralRiderFiveRidesReferrerPoints, 0, MAX_REFERRAL_RIDER_POINTS)),
        isIdleWarningMinutesValid: computed(() => isWholeMinute(state.idleWarningMinutes)),
        isIdleAutoOfflineMinutesValid: computed(() => isWholeMinute(state.idleAutoOfflineMinutes)),
        // Mirrors ServiceJurisdictionValidator.validateIdleWindow, and it is not a nicety: ordering
        // alone is not enough, because the sweep skips a prompt with under MIN_IDLE_GAP_SECONDS left,
        // so a window one minute wide is ordered correctly and still never prompts anybody — every
        // driver in this jurisdiction set offline without warning.
        isIdleWindowValid: computed(() => isWholeMinute(state.idleWarningMinutes)
            && isWholeMinute(state.idleAutoOfflineMinutes)
            && (Number(state.idleAutoOfflineMinutes) - Number(state.idleWarningMinutes)) * 60
                > MIN_IDLE_GAP_SECONDS)
    };

    const validateName = () => {
        if (validations.isNameValid.value) {
            $('#name').removeClass('is-invalid');
            $('#name-error').addClass('d-none');
            return true;
        } else {
            $('#name').addClass('is-invalid');
            $('#name-error').removeClass('d-none');
            return false;
        }
    };

    const validateCommissionPercentage = () => {
        if (validations.isCommissionPercentageValid.value) {
            $('#commissionPercentage').removeClass('is-invalid');
            $('#commissionPercentage-error').addClass('d-none');
            return true;
        } else {
            $('#commissionPercentage').addClass('is-invalid');
            $('#commissionPercentage-error').removeClass('d-none');
            return false;
        }
    };

    const validatePointsPerDollar = () => {
        if (validations.isPointsPerDollarValid.value) {
            $('#pointsPerDollar').removeClass('is-invalid');
            $('#pointsPerDollar-error').addClass('d-none');
            return true;
        } else {
            $('#pointsPerDollar').addClass('is-invalid');
            $('#pointsPerDollar-error').removeClass('d-none');
            return false;
        }
    };

    const validateSignupBonusPoints = () => {
        if (validations.isSignupBonusPointsValid.value) {
            $('#signupBonusPoints').removeClass('is-invalid');
            $('#signupBonusPoints-error').addClass('d-none');
            return true;
        } else {
            $('#signupBonusPoints').addClass('is-invalid');
            $('#signupBonusPoints-error').removeClass('d-none');
            return false;
        }
    };

    const validateIdleWarningMinutes = () => {
        if (validations.isIdleWarningMinutesValid.value) {
            $('#idleWarningMinutes').removeClass('is-invalid');
            $('#idleWarningMinutes-error').addClass('d-none');
            return true;
        } else {
            $('#idleWarningMinutes').addClass('is-invalid');
            $('#idleWarningMinutes-error').removeClass('d-none');
            return false;
        }
    };

    const validateMaxPickupDistance = () => {
        if (validations.isMaxPickupDistanceValid.value) {
            $('#maxPickupDistanceMeters').removeClass('is-invalid');
            $('#maxPickupDistanceMeters-error').addClass('d-none');
            return true;
        } else {
            $('#maxPickupDistanceMeters').addClass('is-invalid');
            $('#maxPickupDistanceMeters-error').removeClass('d-none');
            return false;
        }
    };

    const validateIdleWindow = () => {
        if (validations.isIdleWindowValid.value) {
            $('#idleAutoOfflineMinutes').removeClass('is-invalid');
            $('#idleAutoOfflineMinutes-error').addClass('d-none');
            return true;
        } else {
            $('#idleAutoOfflineMinutes').addClass('is-invalid');
            $('#idleAutoOfflineMinutes-error').removeClass('d-none');
            return false;
        }
    };

    const validateSignupBonusWindow = () => {
        if (validations.isSignupBonusWindowValid.value) {
            $('#signupBonusEndsOn').removeClass('is-invalid');
            $('#signupBonusEndsOn-error').addClass('d-none');
            return true;
        } else {
            $('#signupBonusEndsOn').addClass('is-invalid');
            $('#signupBonusEndsOn-error').removeClass('d-none');
            return false;
        }
    };

    const validateReferralDriverDiscountPercent = () => {
        if (validations.isReferralDriverDiscountPercentValid.value) {
            $('#referralDriverDiscountPercent').removeClass('is-invalid');
            $('#referralDriverDiscountPercent-error').addClass('d-none');
            return true;
        }
        $('#referralDriverDiscountPercent').addClass('is-invalid');
        $('#referralDriverDiscountPercent-error').removeClass('d-none');
        return false;
    };

    const validateReferralDriverMaxRedemptions = () => {
        if (validations.isReferralDriverMaxRedemptionsValid.value) {
            $('#referralDriverMaxRedemptions').removeClass('is-invalid');
            $('#referralDriverMaxRedemptions-error').addClass('d-none');
            return true;
        }
        $('#referralDriverMaxRedemptions').addClass('is-invalid');
        $('#referralDriverMaxRedemptions-error').removeClass('d-none');
        return false;
    };

    const validateReferralRiderFirstRideReferrerPoints = () => {
        if (validations.isReferralRiderFirstRideReferrerPointsValid.value) {
            $('#referralRiderFirstRideReferrerPoints').removeClass('is-invalid');
            $('#referralRiderFirstRideReferrerPoints-error').addClass('d-none');
            return true;
        }
        $('#referralRiderFirstRideReferrerPoints').addClass('is-invalid');
        $('#referralRiderFirstRideReferrerPoints-error').removeClass('d-none');
        return false;
    };

    const validateReferralRiderFirstRideReferredPoints = () => {
        if (validations.isReferralRiderFirstRideReferredPointsValid.value) {
            $('#referralRiderFirstRideReferredPoints').removeClass('is-invalid');
            $('#referralRiderFirstRideReferredPoints-error').addClass('d-none');
            return true;
        }
        $('#referralRiderFirstRideReferredPoints').addClass('is-invalid');
        $('#referralRiderFirstRideReferredPoints-error').removeClass('d-none');
        return false;
    };

    const validateReferralRiderFiveRidesReferrerPoints = () => {
        if (validations.isReferralRiderFiveRidesReferrerPointsValid.value) {
            $('#referralRiderFiveRidesReferrerPoints').removeClass('is-invalid');
            $('#referralRiderFiveRidesReferrerPoints-error').addClass('d-none');
            return true;
        }
        $('#referralRiderFiveRidesReferrerPoints').addClass('is-invalid');
        $('#referralRiderFiveRidesReferrerPoints-error').removeClass('d-none');
        return false;
    };

    const validateReferralRiderMaxRewardedReferrals = () => {
        if (validations.isReferralRiderMaxRewardedReferralsValid.value) {
            $('#referralRiderMaxRewardedReferrals').removeClass('is-invalid');
            $('#referralRiderMaxRewardedReferrals-error').addClass('d-none');
            return true;
        }
        $('#referralRiderMaxRewardedReferrals').addClass('is-invalid');
        $('#referralRiderMaxRewardedReferrals-error').removeClass('d-none');
        return false;
    };

    watch(() => state.name, validateName);
    watch(() => state.signupBonusPoints, validateSignupBonusPoints);
    watch(() => state.signupBonusStartsOn, validateSignupBonusWindow);
    watch(() => state.signupBonusEndsOn, validateSignupBonusWindow);
    watch(() => state.pointsPerDollar, validatePointsPerDollar);
    watch(() => state.idleWarningMinutes, () => {
        validateIdleWarningMinutes();
        validateIdleWindow();
    });
    watch(() => state.idleAutoOfflineMinutes, validateIdleWindow);
    watch(() => state.maxPickupDistanceMeters, validateMaxPickupDistance);
    watch(() => state.commissionPercentage, validateCommissionPercentage);
    watch(() => state.referralDriverDiscountPercent, validateReferralDriverDiscountPercent);
    watch(() => state.referralDriverMaxRedemptions, validateReferralDriverMaxRedemptions);
    watch(() => state.referralRiderFirstRideReferrerPoints, validateReferralRiderFirstRideReferrerPoints);
    watch(() => state.referralRiderFirstRideReferredPoints, validateReferralRiderFirstRideReferredPoints);
    watch(() => state.referralRiderFiveRidesReferrerPoints, validateReferralRiderFiveRidesReferrerPoints);
    watch(() => state.referralRiderMaxRewardedReferrals, validateReferralRiderMaxRewardedReferrals);

    watch(() => state.serviceArea, (newValue) => {
        if (newValue && newValue.id) {
            state.geoZoneId = newValue.id;
        } else {
            state.geoZoneId = null;
        }
    });

    const isValid = () => {
        let isNameValid = validateName();
        let isCommissionValid = validateCommissionPercentage();
        let isRateValid = validatePointsPerDollar();
        let isBonusValid = validateSignupBonusPoints();
        let isWindowValid = validateSignupBonusWindow();
        let isIdleWarningValid = validateIdleWarningMinutes();
        let isIdleWindowValid = validateIdleWindow();
        // isMaxPickupDistanceAboveAdvisory is deliberately absent: it is a warning, and folding it in
        // here would turn it into a save-blocking rejection.
        let isPickupDistanceValid = validateMaxPickupDistance();
        // Every validator runs before the && chain: short-circuiting would leave later fields
        // un-marked, so an admin fixing the first error would discover the next only on resubmit.
        let referralResults = [validateReferralDriverDiscountPercent(), validateReferralDriverMaxRedemptions(), validateReferralRiderFirstRideReferrerPoints(), validateReferralRiderFirstRideReferredPoints(), validateReferralRiderFiveRidesReferrerPoints(), validateReferralRiderMaxRewardedReferrals()];
        return isNameValid && isCommissionValid && isRateValid && isBonusValid && isWindowValid
            && isIdleWarningValid && isIdleWindowValid && isPickupDistanceValid
            && referralResults.every(Boolean);
    };

    const initializeValidation = () => {
        if (state.name) validateName();
        if (state.commissionPercentage !== undefined) validateCommissionPercentage();
        if (state.pointsPerDollar !== undefined) validatePointsPerDollar();
        if (state.signupBonusPoints !== undefined) validateSignupBonusPoints();
        validateSignupBonusWindow();
        if (state.idleWarningMinutes !== undefined) validateIdleWarningMinutes();
        validateIdleWindow();
        if (state.maxPickupDistanceMeters !== undefined) validateMaxPickupDistance();
        if (state.referralDriverDiscountPercent !== undefined) validateReferralDriverDiscountPercent();
        if (state.referralDriverMaxRedemptions !== undefined) validateReferralDriverMaxRedemptions();
        if (state.referralRiderFirstRideReferrerPoints !== undefined) validateReferralRiderFirstRideReferrerPoints();
        if (state.referralRiderFirstRideReferredPoints !== undefined) validateReferralRiderFirstRideReferredPoints();
        if (state.referralRiderFiveRidesReferrerPoints !== undefined) validateReferralRiderFiveRidesReferrerPoints();
        if (state.referralRiderMaxRewardedReferrals !== undefined) validateReferralRiderMaxRewardedReferrals();
    };

    setTimeout(initializeValidation, 100);

    return {
        state,
        validations,
        isValid,
        validateReferralDriverDiscountPercent,
        validateReferralDriverMaxRedemptions,
        validateReferralRiderFirstRideReferrerPoints,
        validateReferralRiderFirstRideReferredPoints,
        validateReferralRiderFiveRidesReferrerPoints,
        validateReferralRiderMaxRewardedReferrals,

        initializeValidation
    };
}
